<script setup lang="ts">
    import { ref, computed, onMounted } from 'vue';
    import { deleteDomainValidation, fixEmailDomain, getDomainUsage, getDomainValidations, validateEmailDomain } from './Deliverability.access';
    import List from '@shared/components/List/List.vue';
    import Modal from '@shared/components/Modal.vue';
    import BSDropdownButton from '@shared/components/BSDropdownButton.vue';
    import PaginationWithPageSizeSelection from '@shared/components/Pagination/PaginationWithPageSizeSelection.vue';
    import BSFormSearchInDropdown from '@shared/components/BSFormSearchInDropdown.vue';
    import { formatDateTime } from '@shared/utils/formatDateTime';
    import type { DomainValidationListItem, DomainValidationUsage } from './DeliverabilityTypes';

    type ValidityFilter = 'all' | 'any-invalid' | 'spf' | 'dkim-key1' | 'dkim-ug1' | 'dkim-ug2' | 'dmarc';

    const VALIDITY_FILTER_OPTIONS: { value: ValidityFilter; label: string; odata: string }[] = [
        { value: 'all', label: 'All domains', odata: '' },
        {
            value: 'any-invalid',
            label: 'Any invalid',
            odata: '(SpfStatus eq 0 or DkimKey1Status eq 0 or DkimUg1Status eq 0 or DkimUg2Status eq 0 or DmarcStatus eq 0)',
        },
        { value: 'spf', label: 'SPF invalid', odata: 'SpfStatus eq 0' },
        { value: 'dkim-key1', label: 'DKIM Key1 invalid', odata: 'DkimKey1Status eq 0' },
        { value: 'dkim-ug1', label: 'DKIM Ug1 invalid', odata: 'DkimUg1Status eq 0' },
        { value: 'dkim-ug2', label: 'DKIM Ug2 invalid', odata: 'DkimUg2Status eq 0' },
        { value: 'dmarc', label: 'DMARC invalid', odata: 'DmarcStatus eq 0' },
    ];

    const validations = ref<DomainValidationListItem[]>([]);
    const loading = ref(false);
    const error = ref<string | null>(null);
    const currentPage = ref(1);
    const pageSize = ref(50);
    const totalCount = ref(0);
    const searchFilter = ref<string>('');
    const validityFilter = ref<ValidityFilter>('all');
    const orderByColumn = ref<string>('ValidatedAt');
    const orderByDirection = ref<'asc' | 'desc'>('desc');

    const usageModalDomain = ref<string | null>(null);
    const usageModalData = ref<DomainValidationUsage | null>(null);
    const usageModalLoading = ref(false);

    const invalidStats = computed(() => [
        { label: 'SPF', count: validations.value.filter((v) => v.SpfStatus === 0).length },
        { label: 'DKIM Key1', count: validations.value.filter((v) => v.DkimKey1Status === 0).length },
        { label: 'DKIM Ug1', count: validations.value.filter((v) => v.DkimUg1Status === 0).length },
        { label: 'DKIM Ug2', count: validations.value.filter((v) => v.DkimUg2Status === 0).length },
        { label: 'DMARC', count: validations.value.filter((v) => v.DmarcStatus === 0).length },
    ]);

    const activeValidityOption = computed(() => VALIDITY_FILTER_OPTIONS.find((o) => o.value === validityFilter.value)!);
    const isValidityFilterActive = computed(() => validityFilter.value !== 'all');

    const loadValidations = async (page: number = 1) => {
        loading.value = true;
        error.value = null;

        const skip = (page - 1) * pageSize.value;
        const orderBy = `${orderByColumn.value} ${orderByDirection.value}`;

        try {
            const result = await getDomainValidations(skip, pageSize.value, buildODataFilter(), orderBy);
            validations.value = result.Results;
            totalCount.value = result.Count;
            currentPage.value = page;
        } catch (err) {
            error.value = 'Failed to load domain validations. Please try again.';
            console.error('Error loading validations:', err);
            validations.value = [];
            totalCount.value = 0;
        } finally {
            loading.value = false;
        }
    };

    const buildODataFilter = (): string | undefined => {
        const parts: string[] = [];

        if (searchFilter.value) {
            const escaped = searchFilter.value.replace(/'/g, "''");
            parts.push(`substringof('${escaped}', Domain)`);
        }

        if (activeValidityOption.value.odata) {
            parts.push(activeValidityOption.value.odata);
        }

        return parts.length > 0 ? parts.join(' and ') : undefined;
    };

    const handlePageChange = (newPage: number) => {
        loadValidations(newPage);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        pageSize.value = newPageSize;
        loadValidations(1);
    };

    const handleSearch = (newFilter: string) => {
        searchFilter.value = newFilter;
        loadValidations(1);
    };

    const handleOrderChange = (col: string, dir: string) => {
        orderByColumn.value = col;
        orderByDirection.value = dir as 'asc' | 'desc';
        loadValidations(1);
    };

    const setValidityFilter = (value: ValidityFilter) => {
        validityFilter.value = value;
        loadValidations(1);
    };

    const validateSingleDomain = async (domain: string) => {
        try {
            await validateEmailDomain(`test@${domain}`);
            await loadValidations(currentPage.value);
        } catch (err) {
            console.error('Error revalidating domain:', err);
        }
    };

    const openUsageModal = async (domain: string) => {
        usageModalDomain.value = domain;
        usageModalData.value = null;
        usageModalLoading.value = true;
        try {
            usageModalData.value = await getDomainUsage(domain);
        } catch (err) {
            console.error('Error loading domain usage:', err);
        } finally {
            usageModalLoading.value = false;
        }
    };

    const closeUsageModal = () => {
        usageModalDomain.value = null;
        usageModalData.value = null;
    };

    const removeDomain = async (domain: string) => {
        try {
            await deleteDomainValidation(domain);
            await loadValidations(currentPage.value);
        } catch (err) {
            console.error('Error deleting domain validation:', err);
        }
    };

    const fixDomain = async (accountId: string) => {
        try {
            const success = await fixEmailDomain(accountId);
            if (success) await loadValidations(currentPage.value);
        } catch (err) {
            console.error('Error fixing domain:', err);
        }
    };

    const isDomainValid = (v: DomainValidationListItem) =>
        v.SpfStatus !== 0 && v.DkimKey1Status !== 0 && v.DkimUg1Status !== 0 && v.DkimUg2Status !== 0 && v.DmarcStatus !== 0;

    const statusRank = { 'status-not-ok': 2, 'status-warning': 1, 'status-ok': 0 } as Record<string, number>;

    const accountOnlyStatusClass = (a: DomainUsageAccount): string => {
        if (a.Status === 3) return 'status-not-ok';
        if (a.Status === 4) return 'status-warning';
        if (a.IsDeleted) return 'status-not-ok';
        const now = new Date();
        if (a.Start && new Date(a.Start) > now) return 'status-warning';
        if (a.End && new Date(a.End) < now) return 'status-warning';
        return 'status-ok';
    };

    const subscriptionStatusClass = (a: DomainUsageAccount): string => {
        const s = a.SubscriptionStatus;
        if (s === null || s === undefined) return 'status-ok';
        if (s === 4 || s === 5 || s === 7) return 'status-not-ok';
        if (s === 1 || s === 2 || s === 6) return 'status-warning';
        const now = new Date();
        if (a.SubscriptionEnd && new Date(a.SubscriptionEnd) < now) return 'status-warning';
        return 'status-ok';
    };

    const accountStatusClass = (a: DomainUsageAccount): string => {
        const ac = accountOnlyStatusClass(a);
        const sc = subscriptionStatusClass(a);
        return (statusRank[ac] ?? 0) >= (statusRank[sc] ?? 0) ? ac : sc;
    };

    const accountFirstEnd = (a: DomainUsageAccount): string | null => {
        const dates = [a.End, a.SubscriptionEnd].filter(Boolean) as string[];
        if (!dates.length) return null;
        return dates.reduce((earliest, d) => (new Date(d) < new Date(earliest) ? d : earliest));
    };

    const isDomainUnused = (v: DomainValidationListItem) => v.AccountCount === 0 && v.IssueCount === 0;

    const origin = window.location.origin;

    onMounted(() => {
        loadValidations();
    });
</script>

<template>
    <List
        displayAs="table"
        itemId="domain"
        title="Domain Validation Results"
        :totalItemCount="totalCount"
        :hideSearch="true"
        :items="validations"
        :loading="loading"
        :tableHeaders="['Domain', 'Usage', 'SPF', 'DKIM:Key1', 'DKIM:Ug1', 'DKIM:Ug2', 'DMARC', 'Validation Date', 'Actions']"
        :perPage="pageSize"
        :serverSideFilter="true"
        :orderOptions="[
            { key: 'Domain', display: 'Domain' },
            { key: 'ValidatedAt', display: 'Validation Date' },
        ]"
        :orderByValue="'ValidatedAt'"
        :orderByDirection="'desc'"
        @update:order="handleOrderChange">
        <template #header>
            <div class="d-flex align-items-end gap-lg m-sm-t m-md-b">
                <div class="text-align-c">
                    <h4 class="subheading m-0">Total domains</h4>
                    <div class="xxl fw700">{{ totalCount }}</div>
                </div>
                <div style="border-left: 1px solid #ddd; height: 36px; align-self: center"></div>
                <div v-for="stat in invalidStats" :key="stat.label" class="text-align-c">
                    <h4 class="subheading m-0">{{ stat.label }}</h4>
                    <div class="xl fw700" :class="stat.count === 0 ? 'c-success' : 'c-danger'">{{ stat.count }}</div>
                </div>
            </div>
        </template>
        <template #search>
            <BSFormSearchInDropdown class="toolbar-search" :filter="searchFilter" @update:filter="handleSearch" />
        </template>
        <template #toolbar>
            <BSDropdownButton menuClass="dropdown-menu-right" :active="isValidityFilterActive" closeOnClickInside>
                <template #toggle>
                    <span class="glyphicon glyphicon-filter m-xxs-r"></span>
                    {{ isValidityFilterActive ? activeValidityOption.label : 'Filter' }}
                </template>
                <template #content>
                    <li role="presentation" class="dropdown-header">Validity</li>
                    <li v-for="opt in VALIDITY_FILTER_OPTIONS" :key="opt.value" role="presentation">
                        <a role="menuitem" href="#" @click.prevent="setValidityFilter(opt.value)">
                            <span
                                class="glyphicon glyphicon-ok m-xxs-r"
                                :style="{ visibility: validityFilter.value === opt.value ? 'visible' : 'hidden' }"></span>
                            {{ opt.label }}
                        </a>
                    </li>
                </template>
            </BSDropdownButton>
            <button class="btn btn-default" @click="loadValidations(currentPage)" :disabled="loading">
                <i v-if="loading" class="fa fa-spinner fa-spin"></i>
                <i v-else class="fa fa-refresh"></i>
                Refresh
            </button>
        </template>
        <template #item="{ item: validation }">
            <td>
                <strong>{{ validation.Domain }}</strong>
            </td>
            <td>
                <button v-if="!isDomainUnused(validation)" class="btn btn-link btn-sm p-0" @click="openUsageModal(validation.Domain)">
                    <span v-if="validation.AccountCount > 0">{{ validation.AccountCount }} {{ validation.AccountCount === 1 ? 'account' : 'accounts' }}</span>
                    <span v-if="validation.AccountCount > 0 && validation.IssueCount > 0">,</span>
                    <span v-if="validation.IssueCount > 0">{{ validation.IssueCount }} {{ validation.IssueCount === 1 ? 'issue' : 'issues' }}</span>
                </button>
                <span v-else class="c-muted">Unused</span>
            </td>
            <td>
                <span :class="validation.SpfStatus === 0 ? 'c-danger' : validation.SpfStatus === 2 ? 'c-warning' : 'c-success'">
                    <i
                        :class="
                            validation.SpfStatus === 0 ? 'fa fa-times-circle' : validation.SpfStatus === 2 ? 'fa fa-exclamation-circle' : 'fa fa-check-circle'
                        "></i>
                    {{ validation.SpfStatus === 0 ? 'Invalid' : validation.SpfStatus === 2 ? 'Warning' : 'Valid' }}
                </span>
            </td>
            <td>
                <span :class="validation.DkimKey1Status !== 0 ? 'c-success' : 'c-danger'">
                    <i :class="validation.DkimKey1Status !== 0 ? 'fa fa-check-circle' : 'fa fa-times-circle'"></i>
                    {{ validation.DkimKey1Status !== 0 ? 'Valid' : 'Invalid' }}
                </span>
            </td>
            <td>
                <span :class="validation.DkimUg1Status !== 0 ? 'c-success' : 'c-danger'">
                    <i :class="validation.DkimUg1Status !== 0 ? 'fa fa-check-circle' : 'fa fa-times-circle'"></i>
                    {{ validation.DkimUg1Status !== 0 ? 'Valid' : 'Invalid' }}
                </span>
            </td>
            <td>
                <span :class="validation.DkimUg2Status !== 0 ? 'c-success' : 'c-danger'">
                    <i :class="validation.DkimUg2Status !== 0 ? 'fa fa-check-circle' : 'fa fa-times-circle'"></i>
                    {{ validation.DkimUg2Status !== 0 ? 'Valid' : 'Invalid' }}
                </span>
            </td>
            <td>
                <span :class="validation.DmarcStatus !== 0 ? 'c-success' : 'c-danger'">
                    <i :class="validation.DmarcStatus !== 0 ? 'fa fa-check-circle' : 'fa fa-times-circle'"></i>
                    {{ validation.DmarcStatus !== 0 ? 'Valid' : 'Invalid' }}
                </span>
            </td>
            <td>{{ formatDateTime(new Date(validation.ValidatedAt)) }}</td>
            <td class="autofit">
                <button class="btn btn-primary btn-sm" @click="validateSingleDomain(validation.Domain)">
                    <i class="fa fa-refresh"></i>
                    Revalidate
                </button>
                <button v-if="validation.AccountId && !isDomainValid(validation)" class="btn btn-warning btn-sm" @click="fixDomain(validation.AccountId!)">
                    <i class="fa fa-wrench"></i>
                    Fix domain
                </button>
                <button v-if="isDomainUnused(validation)" class="btn btn-danger btn-sm" @click="removeDomain(validation.Domain)">
                    <i class="fa fa-trash"></i>
                    Delete
                </button>
            </td>
        </template>
    </List>

    <PaginationWithPageSizeSelection
        :isDisplayed="totalCount > pageSize"
        :totalItems="totalCount"
        :pageSize="pageSize"
        :currentPage="currentPage"
        @page-changed="handlePageChange"
        @page-size-changed="handlePageSizeChange" />

    <Modal :show="usageModalDomain !== null" :title="`Usage: ${usageModalDomain}`" size="md" @close="closeUsageModal">
        <template #content>
            <div v-if="usageModalLoading" class="text-center p-3">
                <i class="fa fa-spinner fa-spin"></i>
            </div>
            <div v-else-if="usageModalData">
                <div v-if="usageModalData.Accounts.length > 0" class="m-b-2">
                    <h5>Accounts</h5>
                    <table class="table table-condensed">
                        <tbody>
                            <tr v-for="account in usageModalData.Accounts" :key="account.AccountId">
                                <td>
                                    <i class="fa fa-square m-xxs-r" :class="accountStatusClass(account)"></i>
                                    <a :href="`/BackOffice/Accounts/${account.AccountId}`">{{ account.Name }}</a>
                                </td>
                                <td class="c-muted">{{ account.DefaultFromAddress }}</td>
                                <td v-if="accountFirstEnd(account)" class="c-muted c-warning">
                                    Ends {{ formatDateTime(new Date(accountFirstEnd(account)!)) }}
                                </td>
                                <td v-else></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div v-if="usageModalData.Issues.length > 0">
                    <h5>Issues</h5>
                    <table class="table table-condensed">
                        <tbody>
                            <tr v-for="issue in usageModalData.Issues" :key="issue.IssueId">
                                <td>
                                    <a :href="`/Account/${issue.AccountId}?returnurl=${origin}/Issues/${issue.IssueId}`">
                                        {{ issue.Subject || issue.IssueName }}
                                    </a>
                                </td>
                                <td class="c-muted">
                                    {{ issue.AccountName }}
                                    <a :href="`/BackOffice/Accounts/${issue.AccountId}`" class="m-xs-l" title="View account">
                                        <i class="fa fa-external-link"></i>
                                    </a>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </template>
    </Modal>
</template>
